import React, { useEffect, useState } from "react";
import {
  Card,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Skeleton,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "pages/components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "pages/DashboardNavbar";
import EnhancedTableHead from "pages/components/common/EnhancedTableHead";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { AdminQuotationApiService } from "api/adminQuotation.api";
import { QUOTATION_STATUS_LABELS } from "config/statusLabels";
import ConfirmationModal from "pages/components/common/ConfirmationModal";

const QuoteScreen = () => {
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [activeQuoteId, setActiveQuoteId] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const navigate = useNavigate();

  const loadQuotes = async () => {
    try {
      setLoading(true);
      const response = await AdminQuotationApiService.getQuotations({
        pageNumber: 1,
        pageSize: 200,
      });
      const payload = response?.data?.data;
      let list = [];
      if (Array.isArray(payload?.data)) {
        list = payload.data;
      } else if (Array.isArray(payload)) {
        list = payload;
      }
      setQuotations(list);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load quotations");
      setQuotations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQuotes();
  }, []);

  const handleEditQuote = (quoteId) => {
    navigate(`/admin/quotations/create?quoteId=${quoteId}`);
  };

  const handleOpenMenu = (event, quoteId) => {
    setMenuAnchorEl(event.currentTarget);
    setActiveQuoteId(quoteId);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
    setActiveQuoteId(null);
  };

  const handleMenuEdit = () => {
    if (activeQuoteId) {
      handleEditQuote(activeQuoteId);
    }
    handleCloseMenu();
  };

  const handleMenuViewDetails = () => {
    if (activeQuoteId) {
      navigate(`/admin/quotations/${activeQuoteId}/details`);
    }
    handleCloseMenu();
  };

  const handleSendEmail = async () => {
    if (activeQuoteId) {
      setShowConfirmModal(true);
    }
    setMenuAnchorEl(null); // Close menu but keep activeQuoteId for modal
  };

  const handleSendQuotationConfirmed = async () => {
    try {
      setLoading(true);
      await AdminQuotationApiService.sendQuotation(activeQuoteId);
      toast.success("Quotation sent successfully");
      await loadQuotes();
    } catch (error) {
      console.error(error);
      toast.error("Failed to send quotation email");
    } finally {
      setLoading(false);
      setShowConfirmModal(false);
      setActiveQuoteId(null);
    }
  };

  const getCustomerName = (quote) => {
    if (quote?.customerName) return quote.customerName;
    const fullName = [quote?.billingAddress?.firstName, quote?.billingAddress?.lastName]
      .filter(Boolean)
      .join(" ");
    return fullName || quote?.billingAddress?.name || "-";
  };

  const getStatusChip = (status) => {
    const statusLabel =
      QUOTATION_STATUS_LABELS.find((entry) => Number(entry.value) === Number(status))?.label ||
      `Status ${status}`;
    const sxMap = {
      0: { bgcolor: "#f3f4f6", color: "#6b7280", borderRadius: 6 },
      1: { bgcolor: "#f3f4f6", color: "#6b7280", borderRadius: 6 },
      2: { bgcolor: "#fef9c3", color: "#854d0e", borderRadius: 6 },
      3: { bgcolor: "#dcfce7", color: "#15803d", borderRadius: 6 },
      4: { bgcolor: "#fee2e2", color: "#b91c1c", borderRadius: 6 },
      5: { bgcolor: "#fef9c3", color: "#854d0e", borderRadius: 6 },
    };
    const chipSx = sxMap[Number(status)] || {
      bgcolor: "#f3f4f6",
      color: "#6b7280",
      borderRadius: 6,
    };
    return (
      <SoftBox
        px={2}
        py={0.2}
        width={"fit-content"}
        borderRadius={1}
        fontSize="small"
        fontWeight="bold"
        textAlign="center"
        sx={chipSx}
      >
        {statusLabel}
      </SoftBox>
    );
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <Grid container justifyContent="center">
          <Grid item xs={12} md={12}>
            <Card sx={{ padding: 4 }}>
              <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <SoftTypography variant="h4" mb={2}>
                  Quotations
                </SoftTypography>
                <Link to="/admin/quotations/create">
                  <SoftButton variant="outlined" size="small" color="info">
                    + Add Quotation
                  </SoftButton>
                </Link>
              </SoftBox>
              <TableContainer component={Paper} sx={{ mb: 2, boxShadow: "none" }}>
                <Table size="small" sx={{ boxShadow: "none" }}>
                  <EnhancedTableHead
                    headCells={quoteHeadCell}
                    onRequestSort={() => {}}
                    orderBy={null}
                    order={null}
                  />
                  <TableBody>
                    {loading
                      ? ["r1", "r2", "r3", "r4", "r5", "r6"].map((rowKey) => (
                          <TableRow key={`quote-loading-${rowKey}`}>
                            {["c1", "c2", "c3", "c4", "c5", "c6", "c7", "c8"].map((colKey) => (
                              <TableCell key={`quote-loading-cell-${rowKey}-${colKey}`}>
                                {colKey === "c8" ? (
                                  <Skeleton variant="circular" width={26} height={26} />
                                ) : (
                                  <Skeleton
                                    variant="text"
                                    animation="wave"
                                    width={colKey === "c2" ? "80%" : "60%"}
                                    height={24}
                                  />
                                )}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      : quotations.map((quote) => (
                          <TableRow key={quote.id} hover>
                            <TableCell>{quote.id}</TableCell>
                            <TableCell>{getCustomerName(quote)}</TableCell>
                            <TableCell>
                              {Array.isArray(quote.items) ? quote.items.length : 0}
                            </TableCell>
                            <TableCell>${Number(quote.amount || 0).toFixed(2)}</TableCell>
                            <TableCell>{quote.addedByName || "-"}</TableCell>
                            <TableCell>{quote.acceptedByName || "-"}</TableCell>
                            <TableCell>{getStatusChip(quote.status)}</TableCell>
                            <TableCell>
                              <IconButton
                                size="small"
                                onClick={(event) => handleOpenMenu(event, quote.id)}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                    {!loading && quotations.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <SoftTypography variant="button" color="text">
                            No quotations yet. Click "Add Quotation" to create one.
                          </SoftTypography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Menu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleCloseMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
              >
                <MenuItem onClick={handleMenuViewDetails}>View Details</MenuItem>
                {quotations.find((q) => q.id === activeQuoteId)?.status === 0 && (
                  <MenuItem onClick={handleSendEmail}>Send Quotation</MenuItem>
                )}
              </Menu>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
      <ConfirmationModal
        open={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          setActiveQuoteId(null);
        }}
        onConfirm={handleSendQuotationConfirmed}
        title="Send Quotation?"
        message="This will send the quotation details to the customer via email and set the status to 'Sent'."
        confirmButtonText="Send"
      />
    </DashboardLayout>
  );
};

const quoteHeadCell = [
  { id: "id", label: "Quote #", align: "left" },
  { id: "customer", label: "Customer", align: "left" },
  { id: "items", label: "Items", align: "center" },
  { id: "total", label: "Total", align: "right" },
  { id: "addedBy", label: "Added By", align: "left" },
  { id: "acceptedBy", label: "Accepted By", align: "left" },
  { id: "status", label: "Status", align: "center" },
  { id: "actions", label: "Actions", align: "center" },
];

export default QuoteScreen;
